import React from "react";
import { UNSAFE_NavigationContext, type Navigator } from "react-router-dom";

type BlockerTransaction = { retry: () => void };

/**
 * BrowserRouter's navigator is a history@5 object that still supports
 * block(), but react-router 6 no longer exposes it on the Navigator type.
 */
type BlockableNavigator = Navigator & {
  block?: (blocker: (tx: BlockerTransaction) => void) => () => void;
};

/**
 * Confirms in-app navigation and window unload while `when` is true.
 * Use for forms with unsaved changes.
 */
export function useUnsavedChangesGuard(when: boolean, message: string) {
  const { navigator } = React.useContext(UNSAFE_NavigationContext);

  React.useEffect(() => {
    if (!when) return;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Chrome requires returnValue to be set
      event.returnValue = message;
    };
    window.addEventListener("beforeunload", handleBeforeUnload);

    const blockableNavigator = navigator as BlockableNavigator;
    let unblock: (() => void) | undefined;
    if (typeof blockableNavigator.block === "function") {
      unblock = blockableNavigator.block(tx => {
        if (window.confirm(message)) {
          unblock?.();
          tx.retry();
        }
      });
    }

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      unblock?.();
    };
  }, [when, message, navigator]);
}
